import {
  Account,
  AccountEntitlementOptionKeys,
  SymphonyDeploymentLocation,
} from "@services/account/types";
import { Assignee } from "@services/meetings/assignee";
import { AssetTypes } from "@services/settings/utils";
import {
  AuthenticatedUser,
  AccessLevel,
  User,
  AccountsVisibility,
} from "@services/user/types";

export const isAccountReadonly = (
  accounts: Account[],
  accountId: Account["id"]
): boolean => accounts.find(({ id }) => id === accountId)?.readOnly ?? true;

const viewLevels: AccessLevel[] = ["FullAccess", "ViewOnly"];
const canView = (permission?: AccessLevel) =>
  viewLevels.includes(permission ?? "NoAccess");

export const isPWC = (user: AuthenticatedUser | undefined) =>
  user?.account.details.symphonyDeploymentLocation !== undefined &&
  user.account.details.symphonyDeploymentLocation !==
    SymphonyDeploymentLocation.AVISPL;

export const canViewUser = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.users);
export const canViewRoom = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.rooms);
export const canViewDevice = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.devices);
export const canViewAccount = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.accounts);
export const canViewLocation = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.locations);
export const canViewTicket = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.tickets);
export const canViewMeeting = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.meetings) && !isPWC(user);
export const canViewAnalytics = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.analyticsReports);
export const canViewReports = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.reports);
export const canViewGlobalConfiguration = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.globalConfiguration);
export const canViewAccountConfiguration = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.accountConfiguration);
export const canViewWorkflows = canViewAccountConfiguration;
export const canViewRoleAndCapabilities = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.rolesCapabilities);
export const canViewElogsAndRevisions = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.meetingsElogsAndRevisions);
export const canViewAttachments = (user?: AuthenticatedUser) =>
  canView(user?.role.rolePermissions.attachments);
export const userRoleIsLocked = (user?: AuthenticatedUser) =>
  user?.role.locked ?? false;
export const canAssignRolesToUsers = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.assignRolesToUsers ?? false;
export const canEditUser = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.users === "FullAccess";
export const canEditRoom = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.rooms === "FullAccess";
export const canEditDevice = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.devices === "FullAccess";
export const canEditAccount = (user?: AuthenticatedUser) =>
  !isHomeAccountOnly(user) &&
  user?.role.rolePermissions.accounts === "FullAccess";
export const canEditLocation = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.locations === "FullAccess";
export const canEditMeeting = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.meetings === "FullAccess" && !isPWC(user);
export const canEditTicket = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.tickets === "FullAccess";
export const canEditAnalytics = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.analyticsReports === "FullAccess";
export const canEditReports = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.reports === "FullAccess" && user?.author;
export const canEditGlobalConfiguration = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.globalConfiguration === "FullAccess";
export const canEditAccountConfiguration = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.accountConfiguration === "FullAccess";
export const canEditWorkflows = canEditAccountConfiguration;
export const canEditElogsAndRevisions = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.meetingsElogsAndRevisions === "FullAccess";
export const canLinkAccountsToRooms = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.adminAccountsVisibility != "HomeAccountOnly" &&
  user?.role.rolePermissions.rooms == "FullAccess";
export const canProduceMeeting = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.produceMeetings === true;
export const canEditRole = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.rolesCapabilities === "FullAccess";
export const canUploadAttachments = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.attachments === "FullAccess";
export const canLinkUsers = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.linkAccountsToUsers === true;
export const canAssignProducer = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.assignProducer === true;
export const canAssignAuthor = (user?: AuthenticatedUser) =>
  user?.role?.rolePermissions.assignAuthorToUsers === true;

export const hasLicense = (
  account: User["account"] = undefined,
  key: AccountEntitlementOptionKeys
) => account?.accountEntitlementOptions?.options?.[key]?.enabled ?? false;
export const hasDeviceArchiveLicense = (account: User["account"] = undefined) =>
  hasLicense(account, "deviceArchive");

export const hasControllerSupportLicense = (
  account: User["account"] = undefined
) => hasLicense(account, "supportController");
export const hasDMCASupportLicense = (account: User["account"] = undefined) =>
  hasLicense(account, "supportDmca");
export const hasAggregatorSupportLicense = (
  account: User["account"] = undefined
) => hasLicense(account, "supportAggregator");
export const hasMultipleSourceSupportLicense = (
  account: User["account"] = undefined
) => hasLicense(account, "supportMultipleMonitoringSources");

export const hasExtendedPropertyMappingLicense = (
  account: User["account"] = undefined
) => hasLicense(account, "extendedPropertyMapping");

export const hasWorkflowsLicense = (account: User["account"] = undefined) =>
  hasLicense(account, "workflows");

export const hasAuditTrailsLicense = (account: User["account"] = undefined) =>
  hasLicense(account, "auditTrails");

export const hasExpiredLicense = (account: User["account"] = undefined) =>
  account?.accountEntitlementOptions?.dateExpired === undefined ||
  account?.accountEntitlementOptions?.dateExpired < new Date().getTime();

export const isAdmin = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.admin ?? false;

export const isHomeAccountOnly = (user?: AuthenticatedUser) =>
  user?.role?.rolePermissions?.adminAccountsVisibility ===
  AccountsVisibility.HomeAccountOnly;

export const isSuperAdmin = (user?: AuthenticatedUser) =>
  user?.role?.rolePermissions.adminAccountsVisibility ==
  AccountsVisibility.AllAccounts;

export const isHomeTenantAccount = (user?: AuthenticatedUser) =>
  user?.role?.rolePermissions?.adminAccountsVisibility ==
  AccountsVisibility.HomeTenantAccounts;

export const canEditConfiguration = (
  scope: AssetTypes | "global",
  user?: AuthenticatedUser
) => {
  switch (scope) {
    case "global":
      return canEditAccountConfiguration(user);
    case "account":
      return canEditAccount(user) || isHomeAccountOnly(user);
    case "device":
      return canEditDevice(user);
    case "room":
      return canEditRoom(user);
    case "location":
      return canEditLocation(user);
    default:
      return false;
  }
};

export const canBulkChange = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.allowBulkChanges ?? false;

const canControlDevices = (user?: AuthenticatedUser) =>
  user?.role.rolePermissions.controlDevices ?? false;

export const hasDeviceControlsLicense = (
  account: User["account"] = undefined
) => hasLicense(account, "deviceControls");

export const hasDeviceControlsAccess = (
  user?: AuthenticatedUser,
  account: User["account"] = undefined
) => hasDeviceControlsLicense(account) && canControlDevices(user);

export const hasAssetTaggingLicense = (account: User["account"] = undefined) =>
  hasLicense(account, "assetTagging");
